import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export type ConfirmDialogOptions = {
  title?: string;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
};

const DEFAULT_TITLE = 'Please confirm';

export function useConfirmDialog(defaultOptions?: Partial<ConfirmDialogOptions>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions>({
    title: defaultOptions?.title ?? DEFAULT_TITLE,
    description: defaultOptions?.description ?? '',
    confirmText: defaultOptions?.confirmText,
    cancelText: defaultOptions?.cancelText,
    variant: defaultOptions?.variant ?? 'default',
  });
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback(
    (input: ConfirmDialogOptions | string) => {
      const resolved = typeof input === 'string' ? { description: input } : input;

      setOptions({
        title: resolved.title ?? defaultOptions?.title ?? DEFAULT_TITLE,
        description: resolved.description,
        confirmText: resolved.confirmText ?? defaultOptions?.confirmText,
        cancelText: resolved.cancelText ?? defaultOptions?.cancelText,
        variant: resolved.variant ?? defaultOptions?.variant ?? 'default',
      });
      setOpen(true);

      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [defaultOptions]
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  }, []);

  const dialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={options.title ?? DEFAULT_TITLE}
      description={options.description}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      onConfirm={handleConfirm}
      variant={options.variant}
    />
  );

  return { confirm, dialog };
}
